import './OperatorValueRangeControl.scss';

import React, { useCallback, useState } from 'react';

import type { FilterConfig } from '../../../../../apis/graphql/types';
import { FilterOperator } from '../../../../../apis/graphql/types';
import type { DropdownOption } from '../../../../../common/Dropdown';
import Dropdown from '../../../../../common/Dropdown';
import NumberInput from '../../../../../common/NumberInput/NumberInput';
import bem from '../../../../../utils/bem';
import useScrollAwareMenuPlacement from '../../hooks/useScrollAwareMenuPlacement';

const OPERATOR_LABELS: Record<string, string> = {
  EQUALS: 'Equals',
  NOT_EQUALS: 'Not equal to',
  GREATER_THAN: 'Greater than',
  LESS_THAN: 'Less than',
  GREATER_THAN_OR_EQUAL_TO: 'Greater than or equal to',
  LESS_THAN_OR_EQUAL_TO: 'Less than or equal to',
  BETWEEN: 'Between',
  IN: 'In',
  NOT_IN: 'Not in',
  // KATANA-1811: copy pending Product ("is empty" / "is blank" / "has no value"). The wire value is
  // IS_EMPTY regardless, so a late change is these two lines plus EXISTENCE_CHIP_TEXT in chipUtils.
  IS_EMPTY: 'Is empty',
  IS_NOT_EMPTY: 'Is not empty',
};

// Operators that take no value: the row is complete once one is picked, so the number input is hidden.
const BOUNDLESS_OPERATORS: ReadonlySet<FilterOperator> = new Set([FilterOperator.IsEmpty, FilterOperator.IsNotEmpty]);

const isBoundlessOperator = (operator: FilterOperator | null): boolean =>
  operator !== null && BOUNDLESS_OPERATORS.has(operator);

const DEFAULT_MAX = 99999999999;
const DEFAULT_MIN = 0;

export type OperatorValueState = {
  operator: FilterOperator | null;
  value: number | null;
};

export type OperatorValueRangeControlProps = {
  config: FilterConfig;
  value: OperatorValueState;
  onChange: (id: string, value: OperatorValueState) => void;
  decimals?: number;
};

const [block, element] = bem('operator-value-range-control');

function buildOperatorOptions(operators: FilterConfig['operators']): DropdownOption<FilterOperator>[] {
  if (!operators) return [];
  return operators.map((op) => ({
    value: op,
    label: OPERATOR_LABELS[op] ?? op,
  }));
}

/**
 * Resolves one edge of the input window from the served filter config (KATANA-1812).
 *
 * campaign-graphql authors the bounds and always sends both keys, so the three cases are distinct and
 * must stay distinct: a number is the limit, `null` was authored as "no limit" and is passed through
 * to NumberInput as unbounded, and `undefined` means the field never arrived at all — a stale or
 * partial config — which falls back to the safety-net default rather than silently unbounding the
 * input. Collapsing null and undefined with `??` is the bug this replaces.
 */
function resolveBound(bound: number | null | undefined, fallback: number): number | null {
  return bound === undefined ? fallback : bound;
}

const OperatorValueRangeControl = ({
  config,
  value,
  onChange,
  decimals = config.valueDecimals ?? 0,
}: OperatorValueRangeControlProps): JSX.Element => {
  const { menuPlacement, maxMenuHeight, placementRef, onOpenMenu } = useScrollAwareMenuPlacement({
    hasSearchBar: false,
  });
  const operatorOptions = buildOperatorOptions(config.operators);

  const [state, setState] = useState<OperatorValueState>({
    operator: value.operator,
    value: value.value,
  });

  const minBound = resolveBound(config.rangeConfig?.minPossibleValue, DEFAULT_MIN);
  const maxBound = resolveBound(config.rangeConfig?.maxPossibleValue, DEFAULT_MAX);

  const handleOperatorChange = useCallback(
    (option: DropdownOption<FilterOperator> | null): void => {
      const operator = option?.value ?? null;
      // A bound-less operator drops any typed value, so a hidden number cannot resurface as the
      // bound if the user switches back to a comparison operator.
      const updated: OperatorValueState = { operator, value: isBoundlessOperator(operator) ? null : state.value };
      setState(updated);
      onChange(config.id, updated);
    },
    [config.id, onChange, state]
  );

  const handleValueChange = useCallback(
    (newValue: number | null): void => {
      const updated: OperatorValueState = { ...state, value: newValue };
      setState(updated);
      onChange(config.id, updated);
    },
    [config.id, onChange, state]
  );

  const selectedOption = state.operator ? operatorOptions.find((o) => o.value === state.operator) ?? null : null;

  return (
    <div className={block()} data-testid="operator-value-range-control" ref={placementRef} onMouseDown={onOpenMenu}>
      <div className={element('operator-dropdown')}>
        <Dropdown
          value={selectedOption}
          options={operatorOptions}
          onChange={handleOperatorChange}
          isSearchable={false}
          dataTestId="operator-value-range-operator"
          menuPlacement={menuPlacement}
          maxMenuHeight={maxMenuHeight}
        />
      </div>
      {!isBoundlessOperator(state.operator) && (
        <div className={element('value-wrapper')}>
          <NumberInput
            value={state.value}
            min={minBound}
            max={maxBound}
            decimals={decimals}
            emptyValue={null}
            onChange={handleValueChange}
            prefixIcon={
              config.valuePrefix ? (
                <span className={element('prefix')} data-testid="operator-value-range-prefix">
                  {config.valuePrefix}
                </span>
              ) : undefined
            }
            icon={
              config.valueSuffix ? (
                <span className={element('suffix')} data-testid="operator-value-range-suffix">
                  {config.valueSuffix}
                </span>
              ) : undefined
            }
          />
        </div>
      )}
    </div>
  );
};

export default OperatorValueRangeControl;
